import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Alert } from "@/ui/alert";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { sendResetLink, getUserIdByEmail } from "../../Services/LoginService";

interface ForgotPasswordProps {
  setIsLoader: (v: boolean) => void;
}

const schema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
});

type FormValues = z.infer<typeof schema>;

/**
 * Forgot Password form (react-hook-form + zod, shadcn UI; behavior unchanged)
 * - Keeps existing API flow (getUserIdByEmail -> sendResetLink)
 * - Keeps setIsLoader usage for outer overlay
 * - Uses compact one-line shadcn Alert for messages
 */
const ForgotPassword: React.FC<ForgotPasswordProps> = ({ setIsLoader }) => {
  const [alert, setAlert] = React.useState<{
    type: "danger" | "success";
    message: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const onSubmit = async ({ email }: FormValues) => {
    setAlert(null);
    setIsLoader(true);
    try {
      const resp = await getUserIdByEmail({ data: { email } });

      if (!resp?.IsSuccess) {
        setAlert({
          type: "danger",
          message: resp?.StatusDesc ?? "Request failed",
        });
        return;
      }

      const payload = { UserId: resp?.Payload, EmailAddress: email };
      const sent = await sendResetLink({ data: payload });

      if (!sent?.IsSuccess) {
        setAlert({
          type: "danger",
          message: sent?.StatusDesc ?? "Could not send reset email",
        });
      } else {
        setAlert({
          type: "success",
          message: "Password has been sent successfully to your email address.",
        });
      }
    } catch {
      setAlert({
        type: "danger",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {alert && (
        <Alert
          variant={alert.type === "danger" ? "destructive" : "default"}
          role={alert.type === "danger" ? "alert" : "status"}
          className="p-2"
        >
          <div className="flex items-center gap-2 text-sm min-w-0">
            {alert.type === "danger" ? (
              <AlertCircle className="h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
            )}
            <span className="truncate" title={alert.message}>
              {alert.message}
            </span>
          </div>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="email" className="font-medium text-foreground">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          aria-invalid={errors.email ? "true" : "false"}
          aria-describedby={errors.email ? "email-error" : undefined}
          {...register("email")}
        />
        {errors.email && (
          <p id="email-error" className="text-sm text-destructive">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="mt-6">
        <Button
          type="submit"
          fullWidth
          size="lg"
          variant="primary"
          disabled={isSubmitting}
        >
          Reset Password
        </Button>
      </div>
    </form>
  );
};

export default ForgotPassword;
